import React, {useEffect, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {baseUrl, userName} from "../Univ.js";
import "./IoTStyles.css";
function IoTDeviceMapping() {
    // ============================================================
    // STATE
    // ============================================================
    const [devices, setDevices] = useState([]);
    const [patients, setPatients] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState("");
    const [selectedPatient, setSelectedPatient] = useState("");
    const [selectedIpAdmit, setSelectedIpAdmit] = useState("");
    const [search, setSearch] = useState("");
    const [bed, setBed] = useState("");
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [loadingPatients, setLoadingPatients] = useState(false);
    const [mapping, setMapping] = useState(false);
    // ============================================================
    // LOAD DEVICES
    // ============================================================
    useEffect(() => {
        fetchDevices();
    }, []);
    const fetchDevices = async () => {
        try {
            setLoadingDevices(true);
            const response = await axios.get(baseUrl + "iot-devices/");
            console.log("IoT DEVICES:", response.data);
            /*
             * DRF pagination support
             */
            if (Array.isArray(response.data)) {
                setDevices(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setDevices(response.data.results);
            } else {
                setDevices([]);
            }
        } catch (error) {
            console.log("DEVICE LOAD ERROR:", error);
            Swal.fire("Error", "Unable to load IoT devices.", "error");
        } finally {
            setLoadingDevices(false);
        }
    };
    // ============================================================
    // SEARCH PATIENT / IP ADMISSION
    // ============================================================
    const searchPatients = async () => {
        if (!search.trim()) {
            Swal.fire("Search", "Enter Patient ID, IP ID, mobile number or patient name.", "info");
            return;
        }
        try {
            setLoadingPatients(true);
            const response = await axios.get(baseUrl + "ip-pat-search/?search=" + encodeURIComponent(search));
            console.log("PATIENT SEARCH:", response.data);
            if (Array.isArray(response.data)) {
                setPatients(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setPatients(response.data.results);
            } else {
                setPatients([]);
            }
        } catch (error) {
            console.log("PATIENT SEARCH ERROR:", error);
            Swal.fire("Error", "Unable to search patients.", "error");
        } finally {
            setLoadingPatients(false);
        }
    };
    // ============================================================
    // SELECT DEVICE
    // ============================================================
    const handleDeviceChange = (event) => {
        const deviceId = event.target.value;
        setSelectedDevice(deviceId);
        /*
         * Find complete device object
         */
        const device = devices.find(item => String(item.id) === String(deviceId));
        if (device) {
            /*
             * Automatically suggest device bed
             */
            setBed(device.bed || "");
        }
    };
    // ============================================================
    // SELECT PATIENT
    // ============================================================
    const selectPatient = (patient) => {
        console.log("SELECTED IP PATIENT:", patient);
        /*
         * IMPORTANT
         *
         * Your IpAdmit serializer may return:
         *
         * pat = patient object
         *
         * OR
         *
         * pat = patient ID
         *
         * OR
         *
         * pat_id = patient ID
         */
        let patientId = "";
        let patientName = "";
        let ipAdmissionId = "";
        // --------------------------------------------------------
        // IP ADMISSION ID
        // --------------------------------------------------------
        if (patient.id) {
            ipAdmissionId = patient.id;
        }
        // --------------------------------------------------------
        // PATIENT ID
        // --------------------------------------------------------
        if (patient.pat && typeof patient.pat === "object") {
            patientId = patient.pat.id;
            patientName = patient.pat.patient || patient.pat.pat_id || "";
        } else if (patient.pat && typeof patient.pat !== "object") {
            patientId = patient.pat;
        } else if (patient.pat_id) {
            patientId = patient.pat_id;
        }
        /*
         * If serializer returns nested patient object
         */
        if (!patientName && patient.patient && typeof patient.patient !== "object") {
            patientName = patient.patient;
        }
        console.log("PATIENT ID:", patientId);
        console.log("IP ADMISSION:", ipAdmissionId);
        setSelectedPatient(patientId);
        setSelectedIpAdmit(ipAdmissionId);
        /*
         * If IP admission contains bed
         */
        if (patient.bed) {
            setBed(patient.bed);
        }
    };
    // ============================================================
    // GET PATIENT DISPLAY NAME
    // ============================================================
    const getPatientName = (patient) => {
        /*
         * Nested patient object
         */
        if (patient.pat && typeof patient.pat === "object") {
            return (patient.pat.patient || patient.pat.pat_id || "-");
        }
        /*
         * Normal patient field
         */
        if (patient.patient && typeof patient.patient !== "object") {
            return patient.patient;
        }
        /*
         * Flat fields
         */
        if (patient.pat__patient) {
            return patient.pat__patient;
        }
        if (patient.pat_id) {
            return patient.pat_id;
        }
        return "-";
    };
    // ============================================================
    // GET PATIENT ID
    // ============================================================
    const getPatientId = (patient) => {
        /*
         * Nested patient object
         */
        if (patient.pat && typeof patient.pat === "object") {
            return (patient.pat.pat_id || patient.pat.id || "-");
        }
        /*
         * Serializer flattened response
         */
        if (patient.pat__pat_id) {
            return patient.pat__pat_id;
        }
        if (patient.pat_id) {
            return patient.pat_id;
        }
        return "-";
    };
    // ============================================================
    // GET MOBILE
    // ============================================================
    const getMobile = (patient) => {
        if (patient.pat && typeof patient.pat === "object") {
            return (patient.pat.mobile || "-");
        }
        if (patient.pat__mobile) {
            return patient.pat__mobile;
        }
        if (patient.mobile) {
            return patient.mobile;
        }
        return "-";
    };
    // ============================================================
    // GET IP ID
    // ============================================================
    const getIpId = (patient) => {
        if (patient.ip_id) {
            return patient.ip_id;
        }
        return "-";
    };
    // ============================================================
    // MAP DEVICE
    // ============================================================
    const mapDevice = async () => {
        if (!selectedDevice) {
            Swal.fire("Required", "Please select an IoT device.", "warning");
            return;
        }
        if (!selectedPatient) {
            Swal.fire("Required", "Please select a patient.", "warning");
            return;
        }
        if (!selectedIpAdmit) {
            Swal.fire("Required", "IP Admission is required.", "warning");
            return;
        }
        try {
            setMapping(true);
            const payload = {
                device: Number(selectedDevice),
                patient: Number(selectedPatient),
                ip_admit: Number(selectedIpAdmit),
                bed: bed || "",
                mapped_by: userName || "SYSTEM",
                active: true
            };
            console.log("IOT MAPPING PAYLOAD:", payload);
            const response = await axios.post(baseUrl + "iot-device-mapping/", payload);
            console.log("MAPPING RESPONSE:", response.data);
            if (response.status === 200 || response.status === 201) {
                Swal.fire({
                    title: "Device Mapped Successfully",
                    text: "IoT device is now connected to the patient.",
                    icon: "success",
                    timer: 2500,
                    showConfirmButton: false
                });
                /*
                 * Reset selection
                 */
                setSelectedDevice("");
                setSelectedPatient("");
                setSelectedIpAdmit("");
                setBed("");
                setPatients([]);
                setSearch("");
                /*
                 * Refresh devices
                 */
                fetchDevices();
            }
        } catch (error) {
            console.log("MAPPING ERROR:", error);
            let message = "Unable to map IoT device to patient.";
            if (error.response && error.response.data) {
                console.log("SERVER RESPONSE:", error.response.data);
                if (error.response.data.detail) {
                    message = error.response.data.detail;
                } else {
                    message = JSON.stringify(error.response.data);
                }
            }
            Swal.fire("Mapping Error", message, "error");
        } finally {
            setMapping(false);
        }
    };
    // ============================================================
    // SELECTED DEVICE OBJECT
    // ============================================================
    const selectedDeviceObject = devices.find(item => String(item.id) === String(selectedDevice));
    // ============================================================
    // UI
    // ============================================================
    return (<section className="container mt-4">

        {/* ====================================================
                HEADER
            ==================================================== */}

        <div className="iot-page-header">

            <div>

                <h3>
                    IoT Device → Patient Mapping
                </h3>

                <p>
                    Connect an IoT device to an active
                    inpatient admission
                </p>

            </div>

        </div>


        {/* ====================================================
                DEVICE SELECTION
            ==================================================== */}

        <div className="card shadow-sm mb-4">

            <div className="card-header">

                <strong>
                    1. Select IoT Device
                </strong>

            </div>


            <div className="card-body">

                <div className="row">

                    <div className="col-md-8">

                        <label className="form-label">

                            IoT Device *

                        </label>


                        <select

                            className="form-control"

                            value={selectedDevice}

                            onChange={handleDeviceChange}

                            disabled={loadingDevices}

                        >

                            <option value="">

                                {loadingDevices ? "Loading devices..." : "Select IoT Device"}

                            </option>


                            {devices.map(device => (<option

                                key={device.id}

                                value={device.id}

                            >

                                {device.device_id}
                                {" - "}
                                {device.device_name}
                                {" - "}
                                {device.device_type}
                                {" - "}
                                {device.ward || "No Ward"}
                                {" - Bed "}
                                {device.bed || "-"}

                            </option>))}

                        </select>

                    </div>


                    <div className="col-md-4">

                        <label className="form-label">

                            Device Status

                        </label>


                        <div className="form-control bg-light">

                            {selectedDeviceObject ? (<span>

                                        {selectedDeviceObject.is_online ? (<span className="text-success">

                                                ● ONLINE

                                            </span>) : (<span className="text-danger">

                                                ● OFFLINE

                                            </span>)}

                                    </span>) : (<span className="text-muted">

                                        Select device

                                    </span>)}

                        </div>

                    </div>

                </div>


                {selectedDeviceObject && (<div className="row mt-3">

                    <div className="col-md-3">

                        <small className="text-muted">

                            Device ID

                        </small>

                        <div>

                            {selectedDeviceObject.device_id}

                        </div>

                    </div>


                    <div className="col-md-3">

                        <small className="text-muted">

                            Manufacturer

                        </small>

                        <div>

                            {selectedDeviceObject.manufacturer || "-"}

                        </div>

                    </div>


                    <div className="col-md-3">

                        <small className="text-muted">

                            Model

                        </small>

                        <div>

                            {selectedDeviceObject.model_number || "-"}

                        </div>

                    </div>


                    <div className="col-md-3">

                        <small className="text-muted">

                            Protocol

                        </small>

                        <div>

                            {selectedDeviceObject.protocol || "-"}

                        </div>

                    </div>

                </div>)}

            </div>

        </div>


        {/* ====================================================
                PATIENT SEARCH
            ==================================================== */}

        <div className="card shadow-sm mb-4">

            <div className="card-header">

                <strong>
                    2. Select Active Patient Admission
                </strong>

            </div>


            <div className="card-body">

                <div className="row">

                    <div className="col-md-9">

                        <input

                            type="text"

                            className="form-control"

                            placeholder={"Search Patient ID, IP ID, Mobile or Name"}

                            value={search}

                            onChange={e => setSearch(e.target.value)}

                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    searchPatients();
                                }
                            }}

                        />

                    </div>


                    <div className="col-md-3">

                        <button

                            className="btn btn-primary w-100"

                            onClick={searchPatients}

                            disabled={loadingPatients}

                        >

                            {loadingPatients ? "Searching..." : "Search Patient"}

                        </button>

                    </div>

                </div>


                {/* =================================================
                        SEARCH RESULTS
                    ================================================= */}

                {patients.length > 0 && (<div className="table-responsive mt-4">

                    <table className="table table-hover">

                        <thead>

                        <tr>

                            <th>
                                IP ID
                            </th>

                            <th>
                                Patient
                            </th>

                            <th>
                                Patient ID
                            </th>

                            <th>
                                Mobile
                            </th>

                            <th>
                                Action
                            </th>

                        </tr>

                        </thead>


                        <tbody>

                        {patients.map((patient, index) => (<tr

                            key={patient.id || index}

                        >

                            <td>

                                {getIpId(patient)}

                            </td>


                            <td>

                                {getPatientName(patient)}

                            </td>


                            <td>

                                {getPatientId(patient)}

                            </td>


                            <td>

                                {getMobile(patient)}

                            </td>


                            <td>

                                <button

                                    className="btn btn-success btn-sm"

                                    onClick={() => selectPatient(patient)}

                                >

                                    Select

                                </button>

                            </td>

                        </tr>))}

                        </tbody>

                    </table>

                </div>)}


                {patients.length === 0 && search && !loadingPatients && (<div className="alert alert-info mt-4">

                    No active inpatient admission
                    found.

                </div>)}

            </div>

        </div>


        {/* ====================================================
                MAPPING CONFIRMATION
            ==================================================== */}

        <div className="card shadow-sm mb-4">

            <div className="card-header">

                <strong>
                    3. Confirm Mapping
                </strong>

            </div>


            <div className="card-body">

                <div className="row">

                    {/* DEVICE */}

                    <div className="col-md-6 mb-3">

                        <label className="form-label">

                            IoT Device

                        </label>


                        <input

                            type="text"

                            className="form-control"

                            value={selectedDeviceObject ? (selectedDeviceObject.device_id + " - " + selectedDeviceObject.device_name) : ""}

                            placeholder="Select device above"

                            readOnly

                        />

                    </div>


                    {/* PATIENT */}

                    <div className="col-md-6 mb-3">

                        <label className="form-label">

                            Patient ID

                        </label>


                        <input

                            type="text"

                            className="form-control"

                            value={selectedPatient}

                            placeholder="Select patient above"

                            readOnly

                        />

                    </div>


                    {/* IP ADMISSION */}

                    <div className="col-md-6 mb-3">

                        <label className="form-label">

                            IP Admission

                        </label>


                        <input

                            type="text"

                            className="form-control"

                            value={selectedIpAdmit}

                            placeholder="IP admission"

                            readOnly

                        />

                    </div>


                    {/* BED */}

                    <div className="col-md-6 mb-3">

                        <label className="form-label">

                            Bed

                        </label>


                        <input

                            type="text"

                            className="form-control"

                            value={bed}

                            onChange={e => setBed(e.target.value)}

                            placeholder="Bed number"

                        />

                    </div>

                </div>


                {/* =================================================
                        SUMMARY
                    ================================================= */}

                {(selectedDevice && selectedPatient) && (<div className="alert alert-success mt-3">

                    <strong>
                        Mapping Summary
                    </strong>

                    <hr/>

                    <div>

                        <strong>
                            Device:
                        </strong>{" "}

                        {selectedDeviceObject ? selectedDeviceObject.device_id : "-"}

                    </div>


                    <div>

                        <strong>
                            Patient:
                        </strong>{" "}

                        {selectedPatient}

                    </div>


                    <div>

                        <strong>
                            IP Admission:
                        </strong>{" "}

                        {selectedIpAdmit}

                    </div>


                    <div>

                        <strong>
                            Bed:
                        </strong>{" "}

                        {bed || "-"}

                    </div>

                </div>)}


                {/* =================================================
                        BUTTONS
                    ================================================= */}

                <div className="text-center mt-4">

                    <button

                        className="btn btn-success me-2"

                        onClick={mapDevice}

                        disabled={mapping || !selectedDevice || !selectedPatient || !selectedIpAdmit}

                    >

                        {mapping ? "Mapping..." : "Map Device to Patient"}

                    </button>


                    <button

                        className="btn btn-secondary"

                        onClick={() => {
                            setSelectedDevice("");
                            setSelectedPatient("");
                            setSelectedIpAdmit("");
                            setBed("");
                            setPatients([]);
                            setSearch("");
                        }}

                        disabled={mapping}

                    >

                        Clear

                    </button>

                </div>

            </div>

        </div>

    </section>);
}
export default IoTDeviceMapping;
